import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import type { BuilderSelection } from "@keydials/dial-workspace";
import { api } from "../auth/AuthContext";
import { AccessEnded } from "../components/AccessEnded";
import { useAuthoring } from "../authoring/AuthoringProvider";
import { BuilderFrame } from "../authoring/BuilderFrame";
import { builderCopy } from "../authoring/copy";
import { SavedIndicator } from "../authoring/SavedIndicator";
import { BuildStage } from "../authoring/stages/BuildStage";
import { ColoursStage } from "../authoring/stages/ColoursStage";
import { DetailsStage } from "../authoring/stages/DetailsStage";
import { LaunchStage } from "../authoring/stages/LaunchStage";
import { StartStage } from "../authoring/stages/StartStage";
import { enterableStages, fixStage, parseStage, parseStart, resolveStage, type BuilderStageId, type StageFacts } from "../authoring/stages";
import { useDraftSession, type DraftPhase } from "../authoring/useDraftSession";

/** Each stage's plain question, shown on its own while there's no draft to put under it (KEY-560). */
const STAGE_QUESTIONS: Record<BuilderStageId, string> = {
  start: builderCopy.start.question,
  build: builderCopy.build.prompt,
  colours: builderCopy.colours.question,
  details: builderCopy.details.question,
  launch: builderCopy.launch.question,
};

function stageFacts(draft: DraftPhase): StageFacts {
  if (draft.phase !== "ready") return { created: false, hasSections: false, lifecycle: null };
  const lifecycle = draft.snapshot.lockedLifecycle ?? draft.draft.lifecycle;
  return {
    created: true,
    hasSections: draft.snapshot.model.sections.length > 0,
    lifecycle: lifecycle === "Checking" || lifecycle === "Published" ? lifecycle : "Draft",
  };
}

/**
 * The dial builder (KEY-560): `/build/new` for a new dial, `/build/:id` to carry on with one, autosaved as the author goes.
 * Anyone the builder isn't offered to is sent to My Dials, and the server refuses its routes regardless.
 */
export function Builder() {
  const authoring = useAuthoring();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const draft = useDraftSession(authoring.offered ? id : undefined);
  // What "Fix this" points Build or Details at. Kept across the move it makes, and cleared by any other move between stages.
  const [fixing, setFixing] = useState<BuilderSelection | null>(null);

  // An author's requests go to the organisation they signed in to. The client already sends their authoring calls without an
  // org, and clearing the active one here stops the org the last dial set reaching anything else the builder reads.
  useEffect(() => {
    api.setActiveOrg(null);
  }, []);

  if (!authoring.loaded) {
    return <main className="kd-builder-loading">{builderCopy.frame.loading}</main>;
  }
  if (!authoring.offered) {
    return <Navigate to="/instances" replace />;
  }
  // The 402 that ends a draft session has already switched the app to the trial-ended screen (KEY-637), so this
  // is not reached in practice; kept so the phase is answered honestly rather than falling through to "could
  // not open".
  if (draft.phase === "trialEnded") {
    return <AccessEnded />;
  }
  if (draft.phase === "notFound") {
    return <Navigate to="/instances" replace />;
  }
  if (draft.phase === "loading") {
    return <main className="kd-builder-loading">{builderCopy.frame.loading}</main>;
  }
  if (draft.phase === "failed") {
    return <main className="kd-builder-loading">{builderCopy.frame.couldNotOpen}</main>;
  }

  const ready = draft.phase === "ready" ? draft : null;
  const facts = stageFacts(draft);
  const stage = resolveStage(parseStage(new URLSearchParams(location.search).get("stage")), facts);
  const goTo = (next: BuilderStageId) => {
    setFixing(null);
    navigate({ search: `?stage=${next}` });
  };
  // "Fix this", from Launch or from a readiness pill: a field is fixed on Details, anything on the wheel on Build.
  const fix = (selection: BuilderSelection) => {
    setFixing(selection);
    navigate({ search: `?stage=${fixStage(selection)}` });
  };

  // Every way out saves first.
  const finishLater = async () => {
    if (ready) await ready.session.flush();
    navigate("/instances");
  };

  return (
    <BuilderFrame
      name={ready?.snapshot.model.name ?? null}
      stage={stage}
      enterable={enterableStages(facts)}
      onStage={goTo}
      onFinishLater={() => void finishLater()}
      saveState={ready ? <SavedIndicator snapshot={ready.snapshot} onRetry={() => ready.session.retry()} /> : undefined}
    >
      {stage === "start" ? (
        ready ? (
          <StartStage mode="editing" session={ready.session} snapshot={ready.snapshot} onNext={() => goTo("build")} />
        ) : (
          <StartStage
            mode="new"
            initialStart={parseStart(new URLSearchParams(location.search).get("start"))}
            onCreated={(created) => navigate(`/build/${created.id}?stage=build`, { replace: true })}
            onLeave={() => navigate("/instances")}
          />
        )
      ) : stage === "build" && ready ? (
        <BuildStage
          session={ready.session}
          snapshot={ready.snapshot}
          readiness={ready.readiness}
          initialSelection={fixing}
          onFix={fix}
          onNext={() => goTo("colours")}
        />
      ) : stage === "colours" && ready ? (
        <ColoursStage session={ready.session} snapshot={ready.snapshot} onNext={() => goTo("details")} />
      ) : stage === "details" && ready ? (
        <DetailsStage
          session={ready.session}
          snapshot={ready.snapshot}
          readiness={ready.readiness}
          initialSelection={fixing?.level === "field" ? fixing : null}
          onFix={fix}
          onNext={() => goTo("launch")}
        />
      ) : stage === "launch" && ready ? (
        <LaunchStage
          id={ready.draft.id}
          session={ready.session}
          snapshot={ready.snapshot}
          readiness={ready.readiness}
          lifecycle={ready.draft.lifecycle}
          checkingInstanceId={ready.draft.checkingInstanceId}
          onOpenTestCopy={(instanceId) => navigate(`/instances/${instanceId}`)}
          onBackToEditing={() => goTo("build")}
          onFix={fix}
          onLive={(instanceId) => navigate(`/instances/${instanceId}/settings/members`)}
          onOpen={(instanceId) => navigate(`/instances/${instanceId}`)}
          onGone={() => navigate("/instances", { replace: true })}
        />
      ) : (
        <section className="kd-builder-panel" aria-labelledby="kd-builder-question">
          <h1 id="kd-builder-question" className="kd-builder-question">
            {STAGE_QUESTIONS[stage]}
          </h1>
        </section>
      )}
    </BuilderFrame>
  );
}
